import theme from 'theme/index'
import {getDeliveries} from 'selectors/deliveries'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {getDateFilters} from 'helpers/dateRange'
import {get, isEmpty, isNil} from 'lodash'
import moment from 'moment'
import {PersonTypes} from 'helpers/quotes'
import {getTimeString} from 'helpers/dates'
import {sortByKey, sortByKeyReverse} from 'helpers/sorting'
import {accountHasFeature, userHasFeature} from 'helpers/user'
import {features} from 'helpers/constants'
import uuid from "helpers/uuid"
import {isIntegratedWithDocuments} from 'helpers/integrations'

const MILLIS_IN_DAY = (1000 * 60 * 60 * 24)
const EVENT_HEIGHT = 28

export const DeliveryType = {
  DELIVERY: 'DELIVERY',
  DELIVERY_THIRD_PARTY: 'DELIVERY_THIRD_PARTY',
  DELIVERY_NOTE: 'DELIVERY_NOTE',
}

export const getDeliveryTypeName = (deliveryType) => {
  switch (deliveryType) {
    case DeliveryType.DELIVERY:
      return 'Delivery'
    case DeliveryType.DELIVERY_THIRD_PARTY:
      return 'Delivery Third Party'
    case DeliveryType.DELIVERY_NOTE:
      return 'Delivery Note'
    default:
      return 'Delivery'
  }
}

export const getDeliveryTypeOptions = () => {
  return [
    {
      value: DeliveryType.DELIVERY,
      label: getDeliveryTypeName(DeliveryType.DELIVERY),
    },
    {
      value: DeliveryType.DELIVERY_NOTE,
      label: getDeliveryTypeName(DeliveryType.DELIVERY_NOTE),
    },
    {
      value: DeliveryType.DELIVERY_THIRD_PARTY,
      label: getDeliveryTypeName(DeliveryType.DELIVERY_THIRD_PARTY),
    },
  ]
}

export const DeliveryStatus = {
  UNSCHEDULED: 'UNSCHEDULED',
  SCHEDULED: 'SCHEDULED',
  WIP: 'WIP',
  ON_HOLD: 'ON_HOLD',
  COMPLETE: 'COMPLETE',
}

export const PodRequirements = {
  NONE: 'NONE',
  PHOTO: 'PHOTO',
  SIGNATURE: 'SIGNATURE',
  PHOTO_AND_SIGNATURE: 'PHOTO_AND_SIGNATURE',
}

export const EstimatedTimeUnits = {
  MINS: {
    id: 1,
    label: 'Min(s)',
    value: 'Min(s)',
  },
  HOURS: {
    id: 2,
    label: 'Hour(s)',
    value: 'Hour(s)',
  },
  DAYS: {
    id: 3,
    label: 'Day(s)',
    value: 'Day(s)',
  },
}

export const getDeliveryStatusName = deliveryStatus => {
  switch (deliveryStatus) {
    case DeliveryStatus.UNSCHEDULED:
      return 'Unscheduled'
    case DeliveryStatus.SCHEDULED:
      return 'Scheduled'
    case DeliveryStatus.WIP:
      return 'WIP'
    case DeliveryStatus.ON_HOLD:
      return 'On Hold'
    case DeliveryStatus.COMPLETE:
      return 'Completed'
    default:
      return 'Unscheduled'
  }
}

export const getDeliveryStatusColor = deliveryStatus => {
  switch (deliveryStatus) {
    case DeliveryStatus.UNSCHEDULED:
      return theme.palette.deliveryStatus.unscheduled
    case DeliveryStatus.SCHEDULED:
      return theme.palette.deliveryStatus.scheduled
    case DeliveryStatus.WIP:
      return theme.palette.deliveryStatus.wip
    case DeliveryStatus.ON_HOLD:
      return theme.palette.deliveryStatus.onHold
    case DeliveryStatus.COMPLETE:
      return theme.palette.deliveryStatus.complete
    default:
      return theme.palette.deliveryStatus.unscheduled
  }
}

export const getDeliveryStatusClassName = deliveryStatus => {
  switch (deliveryStatus) {
    case DeliveryStatus.UNSCHEDULED:
      return 'unscheduled'
    case DeliveryStatus.SCHEDULED:
      return 'scheduled'
    case DeliveryStatus.WIP:
      return 'wip'
    case DeliveryStatus.ON_HOLD:
      return 'onHold'
    case DeliveryStatus.COMPLETE:
      return 'complete'
    default:
      return 'unscheduled'
  }
}

export const defaultDeliveriesCalendarSelectedFields = [
  'work centre',
  'start',
  'end',
  'job board',
  'ref',
  'company',
]

export const defaultDeliveriesCalendarFieldOrder = [
  'work centre',
  {group: ['start', 'end']},
  'job board',
  'ref',
  'company',
  'contact',
  'desc',
]

export const getDeliveryStatusOptions = () => {
  return [
    {
      value: DeliveryStatus.UNSCHEDULED,
      label: getDeliveryStatusName(DeliveryStatus.UNSCHEDULED),
    },
    {
      value: DeliveryStatus.SCHEDULED,
      label: getDeliveryStatusName(DeliveryStatus.SCHEDULED),
    },
    {
      value: DeliveryStatus.WIP,
      label: getDeliveryStatusName(DeliveryStatus.WIP),
    },
    {
      value: DeliveryStatus.ON_HOLD,
      label: getDeliveryStatusName(DeliveryStatus.ON_HOLD),
    },
    {
      value: DeliveryStatus.COMPLETE,
      label: getDeliveryStatusName(DeliveryStatus.COMPLETE),
    },
  ]
}

export const getDeliveryParams = (state) => {
  const filters = getDeliveries(state)
  const {
    deliveryTypes,
    workCentreId,
    deliveryStatus,
    userTypes,
    contacts,
    orderTypeId,
    dateTypes,
  } = filters

  const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
  const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

  const params = {}

  if (!isEmpty(deliveryTypes))
    params.deliveryTypes = deliveryTypes

  if (!isEmpty(workCentreId))
    params.workCentreId = workCentreId

  if (!isEmpty(deliveryStatus))
    params.deliveryStatus = deliveryStatus

  if (!isEmpty(orderTypeId))
    params.orderTypes = orderTypeId

  if (!isEmpty(userTypes) && !isEmpty(contacts)) {
    userTypes.forEach(userType => {
      params[`${userType}Id`] = contacts
    })
  }

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }

  return params
}

export const getDeliveryDescription = (delivery) => {
  const description = get(delivery, 'description', null)
  if (!isEmpty(description))
    return description

  return get(delivery, 'deliveryNote.order.desc', null)
}

export const isDeliveryFieldSelected = (field, selectedFields) => {
  if (isNil(field)) {
    return false
  }
  return selectedFields.includes(field)
}

export const getWorkCentreAbbreviation = (workCentreName) => {
  if (isNil(workCentreName)) {
    return ''
  }
  return workCentreName
      .split(' ')
      .slice(0, 2)
      .map(word => word[0])
      .filter(char => char !== undefined)
      .join('')
}

export const getFlattenedDeliveryFields = (fields) => {
  const flatFields = []

  if (!isNil(fields)) {
    fields.forEach((item) => {
      if (!isNil(item)) {
        if (item.hasOwnProperty('group')) {
          item.group.forEach((field) => flatFields.push(field))
        } else {
          flatFields.push(item)
        }
      }
    })
  }

  return flatFields
}

export const getOrderedSelectedDeliveryFields = (fieldOrder, selectedFields) => {
  return fieldOrder.filter(field =>
      selectedFields.includes(field) && field !== 'end')
}

export const isDeliveryTypeNote = (delivery) => {
  const deliveryType = get(delivery, 'deliveryType')
  return deliveryType === DeliveryType.DELIVERY_NOTE
}

export const isDeliveryDraggable = (delivery, readOnly) => {
  if (readOnly) return false
  if (isDeliveryTypeNote(delivery)) return true

  return delivery.deliveryStatus !== DeliveryStatus.WIP
      && delivery.deliveryStatus !== DeliveryStatus.ON_HOLD
      && delivery.deliveryStatus !== DeliveryStatus.COMPLETE
}

export const getDeliveriesContainerHeightStyle = (window, containerOffset) => {
  return {height: window.innerHeight - containerOffset.top}
}

export const getDeliveryCalendarStartDateTime = (selectedDate, selectedViewType) => {
  switch (selectedViewType) {
    case 'day':
      return moment(selectedDate).startOf('day').valueOf()
    case 'week':
      return moment(selectedDate).startOf('isoWeek').valueOf()
    default:
      return selectedDate.getTime()
  }
}

export const getDeliveryCalendarEndDateTime = (selectedDate, selectedViewType) => {
  switch (selectedViewType) {
    case 'day':
      return moment(selectedDate).endOf('day').valueOf()
    case 'week':
      return moment(selectedDate).endOf('isoWeek').valueOf()
    default:
      return selectedDate
  }
}

export const getDeliveryEventStartDateTime = (delivery, selectedDate, selectedViewType) => {
  const startDate = get(delivery, 'startDate')

  if (selectedViewType !== 'day')
    return startDate

  const dayStart = moment(selectedDate).startOf('day').valueOf()
  return startDate < dayStart
      ? dayStart
      : startDate
}

export const getDeliveryEventEndDateTime = (delivery, selectedDate, selectedViewType) => {
  const finishDate = get(delivery, 'finishDate')

  switch (selectedViewType) {
    case 'day':
      const dayEnd = moment(selectedDate).endOf('day').valueOf()
      return finishDate > dayEnd
          ? dayEnd
          : finishDate
    case 'week':
      const weekEnd = moment(selectedDate).endOf('isoWeek').valueOf()
      return finishDate > weekEnd
          ? weekEnd
          : finishDate
    default:
      return finishDate
  }
}

export const getLoadDeliveryParams = (startDate, finishDate, deliveryFilters) => {
  const {search, statuses, companies, jobTypes, workCentres} = deliveryFilters
  const params = {
    finishedStart: startDate,
    startedEnd: finishDate,
    search,
    deliveryStatus: statuses,
    companies: companies?.map(company => company.value),
    orderTypes: jobTypes,
    workCentreId: workCentres,
  }

  const {contacts, userTypes} = deliveryFilters
  if (!isEmpty(contacts)) {
    params['personId'] = contacts
    const allUserTypes = PersonTypes.map(type => type.value)

    if (!isEmpty(userTypes)) {
      userTypes.forEach(type => {
        params[type] = true
      })
      const nonSelectedUserTypes = allUserTypes.filter(filter => !userTypes.includes(filter))
      nonSelectedUserTypes.forEach(type => {
        params[type] = false
      })
    } else {
      allUserTypes.forEach(type => {
        params[type] = true
      })
    }
  }
  return params
}

export const isToday = (date) => {
  const today = new Date()
  return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
}

export const getDaySpan = (delivery, minDate) => {
  const startDate = isNil(minDate)
      ? delivery.startDate
      : Math.max(delivery.startDate, new Date(minDate).setHours(0, 0, 0, 0))
  const diffTime = Math.abs(delivery.finishDate - startDate)
  return Math.floor(diffTime / MILLIS_IN_DAY) + 1
}

export const getFlattenedDate = (date) => {
  return Math.floor(new Date(date).setHours(12, 0, 0, 0) / MILLIS_IN_DAY)
}

export const getDeliveryEvent = (delivery, minDate) => {
  return {
    ...delivery,
    duration: getDeliveryDuration(delivery),
    startTime: getTimeString(get(delivery, 'startDate')),
    endTime: getTimeString(get(delivery, 'finishDate')),
    daySpan: getDaySpan(delivery, minDate),
  }
}

export const getDeliveryEvents = (deliveries, minDate) => {
  const events = deliveries.map(delivery => getDeliveryEvent(delivery, minDate))
  const orderedEvents = getOrderedDeliveryEvents(events)
  const groupedEvents = groupEventsByDay(orderedEvents, minDate)
  allocateEventSlots(groupedEvents)
  return groupedEvents
}

export const groupEventsByDay = (events, minDate) => {
  const minDayStart = new Date(minDate).setHours(0, 0, 0, 0)
  const minFlattenedDate = getFlattenedDate(minDate)

  return events
      .filter(event => event.finishDate >= minDayStart)
      .reduce((acc, event) => {

        const flattenedDate = Math.max(getFlattenedDate(event.startDate), minFlattenedDate)

        if (!acc[flattenedDate])
          acc[flattenedDate] = []

        acc[flattenedDate].push(event)

        return acc
      }, {})
}

export const allocateEventSlots = (eventsByDate) => {
  const dates = Object.keys(eventsByDate)
  dates.sort((a, b) => parseInt(a) - parseInt(b))

  dates.forEach(date => {
    const eventsForDate = eventsByDate[date]
    allocateEventSlotsForDay(date, eventsForDate, eventsByDate)
  })
}

export const allocateEventSlotsForDay = (date, eventsForDate, eventsByDate) => {
  let currentSlot = 0

  eventsForDate.forEach(event => {
    const thisSlot = findEventSlot(currentSlot, date, eventsByDate)
    event.slot = thisSlot
    event.skipSlots = thisSlot - currentSlot
    currentSlot = thisSlot + 1
  })
}

export const findEventSlot = (targetSlot, date, eventsByDate) => {
  while (isSlotTaken(targetSlot, date, eventsByDate)) {
    targetSlot++
  }
  return targetSlot
}

export const isSlotTaken = (slot, date, eventsByDate) => {
  const eventsInSlot = getPreviousEventsInSlot(slot, date, eventsByDate)
  return eventsInSlot.some(event => isEventOverlappingDate(event, date))
}

export const getPreviousEventsInSlot = (slot, eventDate, eventsByDate) => {
  return Object.keys(eventsByDate)
      .filter(date => date < eventDate)
      .flatMap(date => eventsByDate[date])
      .filter(event => event.slot === slot)
}

export const isEventOverlappingDate = (event, flattenedDate) => {
  const eventStartDate = getFlattenedDate(event.startDate)
  const eventFinishDate = getFlattenedDate(event.finishDate)

  return (
      eventStartDate <= flattenedDate &&
      eventFinishDate >= flattenedDate
  )
}

export const getDeliveryEventsForDate = (deliveryEvents, date) => {
  const deliveryDate = getFlattenedDate(date)
  return deliveryEvents[deliveryDate] || []
}

export const getDeliveriesForWorkCentre = (deliveries, workCentreId) => {
  return deliveries.filter(delivery => delivery.workCentreId === workCentreId)
}

export const getOrderedDeliveryEvents = (deliveries) => {
  return deliveries.sort((a, b) => {
    const result = sortByKey(a, b, 'startDate')
    if (result === 0) {
      return sortByKeyReverse(a, b, 'duration')
    }
    return result
  })
}

export const getDraggableId = (delivery) => {
  return `delivery-${delivery.id}`
}

export const getDeliveryEventStyle = (delivery, draggableStyle, isBeforeDragging, isDragging) => {
  const {daySpan = 1, skipSlots = 0} = delivery

  const width = isDragging
      ? get(draggableStyle, 'width')
      : isBeforeDragging
          ? 'auto'
          : `calc(${daySpan}00% + ${daySpan}px - 10px)`

  const marginTop = skipSlots * EVENT_HEIGHT

  return {
    ...draggableStyle,
    width,
    marginTop,
    padding: '2px 5px',
    position: isDragging ? 'fixed' : 'relative',
  }
}

export const getStartOfWeek = (date) => {
  const SUNDAY = 0
  const MONDAY = 1
  const DAYS_TO_SUBTRACT_FROM_SUNDAY = -6

  const dayOfWeekNumber = date.getDay()
  const daysToMonday = dayOfWeekNumber === SUNDAY
      ? DAYS_TO_SUBTRACT_FROM_SUNDAY
      : MONDAY - dayOfWeekNumber

  const startOfWeek = new Date(date)
  startOfWeek.setDate(date.getDate() + daysToMonday)

  return startOfWeek
}

export const getWeekViewDays = (selectedDate) => {
  const startOfWeek = getStartOfWeek(selectedDate)

  const days = []
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek)
    day.setDate(day.getDate() + i)
    days.push(day)
  }
  return days
}

export const getWeekViewDateFormat = (date) => {
  const weekday = date.toLocaleDateString('en-UK', {weekday: 'short'})
  const dayDate = date.getDate()
  const month = date.toLocaleDateString('en-UK', {month: 'short'})
  const year = date.toLocaleDateString('en-UK', {year: 'numeric'})
  return `${weekday} ${dayDate}-${month} ${year}`
}

export const getDeliveryDuration = (delivery) => {
  const deliveryStartDate = new Date(get(delivery, 'startDate'))
  const deliveryFinishDate = new Date(get(delivery, 'finishDate'))
  return deliveryFinishDate - deliveryStartDate
}

export const getDeliveryTargetWorkCentreId = (destination) => {
  const droppableId = get(destination, 'droppableId', '')
  return parseInt(droppableId.split('-')[1])
}

export const getDeliveryTargetDate = (destination) => {
  const droppableId = get(destination, 'droppableId', '')
  return parseInt(droppableId.split('-')[2])
}

export const getDeliveryTargetStart = (delivery, targetDate) => {
  const deliveryStartDate = new Date(get(delivery, 'startDate'))
  const targetStart = new Date(targetDate)
  targetStart.setHours(deliveryStartDate.getHours())
  targetStart.setMinutes(deliveryStartDate.getMinutes())
  targetStart.setSeconds(deliveryStartDate.getSeconds())
  targetStart.setMilliseconds(deliveryStartDate.getMilliseconds())
  return targetStart.getTime()
}

export const getDeliveryTargetFinish = (delivery, targetStart) => {
  return (new Date(targetStart + getDeliveryDuration(delivery))).getTime()
}

export const getEventWrapperClassName = (isDragging) => {
  return isDragging
      ? 'event-container-dragging'
      : 'event-container'
}

export const getMovedDelivery = (deliveries, draggableId) => {
  const deliveryId = parseInt(draggableId.split('-')[1])
  return deliveries.find(delivery => delivery.id === deliveryId)
}

export const getWorkCentreOptions = (workCentres) => {
  const options = workCentres.map(workCentre => {
    const workCentreId = get(workCentre, 'id')
    const department = get(workCentre, 'department', null)
    const departmentName = get(department, 'name')
    const workCentreName = get(workCentre, 'name')
    const departmentSort = get(department, 'sortOrder')
    const workCentreSort = get(workCentre, 'sortOrder')

    return {
      value: workCentreId,
      key: workCentreId,
      label: getDepartmentWorkCentreName(departmentName, workCentreName),
      departmentSort,
      workCentreSort,
    }
  })
  return sortByDepartmentAndWorkCentre(options)
}

export const getDepartmentWorkCentreName = (departmentName, workCentreName) => {
  return `${departmentName}/${workCentreName}`
}

export const sortByDepartmentAndWorkCentre = (workCentres) => {
  workCentres.sort((a, b) => {
    return a.departmentSort === b.departmentSort
        ? a.workCentreSort - b.workCentreSort
        : a.departmentSort - b.departmentSort
  })
  return workCentres
}

export const getSelectedDeliveryNote = (deliveryNotes, selectedDeliveryNoteId) => {
  return deliveryNotes.find(note => note.id === selectedDeliveryNoteId)
}

export const getSelectedDeliveryNoteRef = (deliveryNotes, selectedDeliveryNoteId) => {
  const selectedDeliveryNote = getSelectedDeliveryNote(deliveryNotes, selectedDeliveryNoteId)
  return !isNil(selectedDeliveryNote) ? get(selectedDeliveryNote, 'reference', '') : 'New'
}

export const getDeliveryAddressPreview = (delivery) => {
  const {address} = delivery
  const fields = [
    get(address, 'address1', null),
    get(address, 'address2', null),
    get(address, 'address3', null),
    get(address, 'city', null),
    get(address, 'country', null),
    get(address, 'postCode', null),
  ]
  return fields.filter(field => !isNil(field) && !isEmpty(field)).join(', ')
}

export const areDeliveryMandatoryFieldsPopulated = (delivery) => {
  const address = get(delivery, 'address', null)
  if (!areDeliveryAddressMandatoryFieldsPopulated(address)) return false

  const deliveryNoteId = get(delivery, 'deliveryNoteId', null)
  const deliveryMethodId = get(delivery, 'deliveryMethodId', null)
  const workCentreId = get(delivery, 'workCentreId', null)
  const startDate = get(delivery, 'startDate', null)
  const finishDate = get(delivery, 'finishDate', null)

  return !(
      isNil(deliveryNoteId)
      || isNil(deliveryMethodId)
      || isNil(workCentreId)
      || isNil(startDate)
      || isNil(finishDate)
  )
}

export const areDeliveryAddressMandatoryFieldsPopulated = (address) => {
  const addressLine1 = get(address, 'address1', null)
  const postCode = get(address, 'postCode', null)
  return !(isEmpty(addressLine1) || isEmpty(postCode))
}

export const getDeliveryAddressValidationMessage = (address) => {
  const addressLine1 = get(address, 'address1', null)
  const postCode = get(address, 'postCode', null)

  if (isEmpty(addressLine1) && isEmpty(postCode)) {
    return 'Address and Post Code are mandatory'
  }
  if (isEmpty(addressLine1)) {
    return 'Address is mandatory'
  }
  if (isEmpty(postCode)) {
    return 'Post Code is mandatory'
  }
  return ''
}

export const getDefaultPodRequirements = (settings) => {
  const isPhotoRequired = get(settings, 'deliveriesDefaultPhotoRequired', false)
  const isSignatureRequired = get(settings, 'deliveriesDefaultSignatureRequired', false)

  if (isPhotoRequired && isSignatureRequired) {
    return PodRequirements.PHOTO_AND_SIGNATURE
  } else if (isPhotoRequired) {
    return PodRequirements.PHOTO
  } else if (isSignatureRequired) {
    return PodRequirements.SIGNATURE
  } else {
    return PodRequirements.NONE
  }
}

export const getPodRequirementOptions = () => {
  return [
    {
      label: 'None',
      value: PodRequirements.NONE,
      disabled: false,
    },
    {
      label: 'Photo',
      value: PodRequirements.PHOTO,
      disabled: false,
    },
    {
      label: 'Signature',
      value: PodRequirements.SIGNATURE,
      disabled: false,
    },
    {
      label: 'Photo & Signature',
      value: PodRequirements.PHOTO_AND_SIGNATURE,
      disabled: false,
    },
  ]
}

export const getDeliveryPodRequirements = (delivery) => {
  const photoRequired = get(delivery, 'photoRequired', false)
  const signatureRequired = get(delivery, 'signatureRequired', false)

  if (photoRequired && signatureRequired) {
    return PodRequirements.PHOTO_AND_SIGNATURE
  } else if (photoRequired) {
    return PodRequirements.PHOTO
  } else if (signatureRequired) {
    return PodRequirements.SIGNATURE
  } else {
    return PodRequirements.NONE
  }
}

export const moveDeliveryOnCalendar = (delivery, start, end, resourceId, selectedViewType, selectedDate) => {
  if (selectedViewType === 'day')
    return moveDeliveryOnDayCalendar(delivery, start, end, resourceId, selectedDate)

  if (selectedViewType === 'month')
    return moveDeliveryOnMonthCalendar(delivery, start, end, resourceId)
}

export const moveDeliveryOnMonthCalendar = (delivery, start, end, resourceId) => {
  return {
    ...delivery,
    startDate: start.getTime(),
    finishDate: end.getTime(),
    workCentreId: !isNil(resourceId) ? resourceId : get(delivery, 'workCentreId'),
  }
}

export const moveDeliveryOnDayCalendar = (delivery, start, end, resourceId, selectedDate) => {
  const calendarStartDate = getDeliveryCalendarStartDateTime(selectedDate, 'day')
  const calendarEndDate = getDeliveryCalendarEndDateTime(selectedDate, 'day')

  const isStartsBeforeCalendar = delivery.startDate < calendarStartDate
  const isEndsAfterCalendar = delivery.finishDate > calendarEndDate

  const workCentreId = !isNil(resourceId)
      ? resourceId
      : get(delivery, 'workCentreId')

  const startDate =
      getMovedStartDateOnDayCalendar(delivery, start, end, isStartsBeforeCalendar, isEndsAfterCalendar)
  const finishDate =
      getMovedFinishDateOnDayCalendar(delivery, start, end, isStartsBeforeCalendar, isEndsAfterCalendar)

  return {
    ...delivery,
    startDate,
    finishDate,
    workCentreId,
  }
}

const getMovedStartDateOnDayCalendar = (delivery, start, end, isStartsBeforeCalendar, isEndsAfterCalendar) => {

  if (isStartsBeforeCalendar && isEndsAfterCalendar)
    return delivery.startDate

  if (!isStartsBeforeCalendar)
    return start.getTime()

  const duration = delivery.finishDate - delivery.startDate
  return end.getTime() - duration
}

const getMovedFinishDateOnDayCalendar = (delivery, start, end, isStartsBeforeCalendar, isEndsAfterCalendar) => {

  if (isStartsBeforeCalendar && isEndsAfterCalendar)
    return delivery.finishDate

  if (!isEndsAfterCalendar)
    return end.getTime()

  const duration = delivery.finishDate - delivery.startDate
  return start.getTime() + duration
}

export const isShowDocumentIntegrationWarning = (user) => {
  return (
      accountHasFeature(user, features.POD_LICENCE) &&
      !isIntegratedWithDocuments(user.defaultDocumentProvider)
  )
}

export const getDocumentIntegrationWarningMessage = () => {
  return 'Connect to a document storage solution to send and receive files for Clarity POD. Google Drive is not supported.'
}

export const getNewOperationForDelivery = (items) => {
  return {
    id: uuid(),
    instruction: `Install / Deliver ${items.length} Delivery Note Item(s)`,
    estimatedTime: 0,
    estimatedTimeUnits: 'Min(s)',
    actTime: '00h 00m',
    status: DeliveryStatus.UNSCHEDULED,
  }
}

export const getNewOperationsForEachDeliveryItem = (items) => {
  return items.filter(item => item.quantity > 0).map(item => {
    return {
      id: uuid(),
      instruction: `${item.quantity} x ${item.description}`,
      estimatedTime: 0,
      estimatedTimeUnits: 'Min(s)',
      actTime: '00h 00m',
      status: DeliveryStatus.UNSCHEDULED,
    }
  })
}

export const getAllEstimatedTimeUnits = () => {
  return Object.values(EstimatedTimeUnits).map(unit => ({
    key: unit.id,
    label: unit.label,
    value: unit.value,
  }))
}

export const getDeliveryStatus = (delivery) => {
  return get(delivery, 'deliveryStatus')
}

export const isDeliveryStatusUnscheduled = (status) => {
  return status === DeliveryStatus.UNSCHEDULED
}

export const isDeliveryUnscheduled = (delivery) => {
  const status = getDeliveryStatus(delivery)
  return isDeliveryStatusUnscheduled(status)
}

export const isDeliveryStatusScheduled = (status) => {
  return status === DeliveryStatus.SCHEDULED
}

export const isDeliveryScheduled = (delivery) => {
  const status = getDeliveryStatus(delivery)
  return isDeliveryStatusScheduled(status)
}

export const isDeliveryStatusWip = (status) => {
  return status === DeliveryStatus.WIP
}

export const isDeliveryWip = (delivery) => {
  const status = getDeliveryStatus(delivery)
  return isDeliveryStatusWip(status)
}

export const isDeliveryStatusOnHold = (status) => {
  return status === DeliveryStatus.ON_HOLD
}

export const isDeliveryOnHold = (delivery) => {
  const status = getDeliveryStatus(delivery)
  return isDeliveryStatusOnHold(status)
}

export const isDeliveryStatusComplete = (status) => {
  return status === DeliveryStatus.COMPLETE
}

export const isDeliveryComplete = (delivery) => {
  const status = getDeliveryStatus(delivery)
  return isDeliveryStatusComplete(status)
}

export const getOperationsForAddRequest = (deliveryStatus, operations) => {
  return operations.map(((operation, index) => {
    return {
      estimatedTime: operation.estimatedTime,
      estimatedTimeUnits: operation.estimatedTimeUnits,
      instruction: operation.instruction,
      sortOrder: index + 1,
      status: isDeliveryStatusScheduled(deliveryStatus) ? DeliveryStatus.SCHEDULED : DeliveryStatus.UNSCHEDULED,
    }
  }))
}

export const getOperationsForUpdateRequest = (operations, deliveryOperationLogs) => {
  return operations.map(((operation, index) => {
    const id = !isNaN(operation.id) ? operation.id : null
    const operationLogs = deliveryOperationLogs.filter(operationLog => {
      operationLog.id = !isNaN(operationLog.id) ? operationLog.id : null
      return !isNaN(operationLog.deliveryOperationId) && operationLog.deliveryOperationId === id
    })
    return {
      id,
      estimatedTime: operation.estimatedTime,
      estimatedTimeUnits: operation.estimatedTimeUnits,
      instruction: operation.instruction,
      notes: operation.notes,
      sortOrder: index + 1,
      status: operation.status,
      deliveryOperationLogs: operationLogs,
    }
  }))
}

export const getDeliveryWithSortedOperations = (delivery) => {
  const operations = get(delivery, 'deliveryOperations', [])
  if (isEmpty(operations)) return delivery

  const mappedOperations = operations
      .sort((a, b) => sortByKey(a, b, 'sortOrder'))
      .map((operation, index) => ({
        ...operation,
        actTime: isEmpty(get(operation, 'deliveryOperationLogs', []))
            ? '00h 00m'
            : getActTime(operation),
        op: index + 1,
      }))

  return {
    ...delivery,
    deliveryOperations: mappedOperations,
  }
}

export const userHasDeliverySchedulingFeature = (user) => {
  return userHasFeature(user, features.DELIVERY_SCHEDULING)
}

export const isCancelButtonVisible = (user) => {
  return userHasDeliverySchedulingFeature(user)
}

export const isSaveButtonVisible = (user) => {
  return userHasDeliverySchedulingFeature(user)
}

export const isCloseButtonVisible = (user) => {
  return !userHasDeliverySchedulingFeature(user)
}

export const sumDates = (dates) => new Date(dates.reduce((acc, date) => acc + (date || 0), 0)).getTime()

export const isScheduleButtonVisible = (delivery, user, isDeliveryNew) => {
  if (!userHasDeliverySchedulingFeature(user)) {
    return false
  }
  return isDeliveryNew || isDeliveryUnscheduled(delivery)
}

export const isUnscheduleButtonVisible = (delivery, user, deliveryOperationLogs) => {
  if (!userHasDeliverySchedulingFeature(user) || !isEmpty(deliveryOperationLogs)) {
    return false
  }
  return isDeliveryScheduled(delivery)
}

export const isDeleteButtonVisible = (delivery, user) => {
  if (!userHasDeliverySchedulingFeature(user)) {
    return false
  }
  return isDeliveryUnscheduled(delivery)
      || isDeliveryScheduled(delivery)
}

export const isAddressReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryScheduled(delivery)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const isPODRequirementsReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryScheduled(delivery)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const isOperationsReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryScheduled(delivery)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const isItemsReadOnly = (isDeliveryNew, delivery, user) => {
  if (isDeliveryNew) return false

  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const isWorkCentreReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const isStartAndEndDateReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryWip(delivery)
      || isDeliveryOnHold(delivery)
      || isDeliveryComplete(delivery)
}

export const getActTime = (operation) => {
  const logs = get(operation, 'deliveryOperationLogs', [])
  const startTimes = logs.map(log => get(log, 'startTime'))
  const finishTimes = logs.map(log => get(log, 'finishTime'))

  const sumStart = sumDates(startTimes)
  const sumEnd = sumDates(finishTimes)

  const duration = sumEnd - sumStart

  return getDurationInHoursAndMinutes(duration)
}

export const getDurationInHoursAndMinutes = (duration) => {
  const millisecondsInHour = 1000 * 60 * 60
  const millisecondsInMinute = 1000 * 60

  const hours = Math.max(0, Math.floor(duration / millisecondsInHour))
  const minutes = Math.max(0, Math.floor((duration % millisecondsInHour) / millisecondsInMinute))
  return `${hours}h ${minutes}m`
}

export const isDeliveryMethodReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryComplete(delivery)
}

export const isTrackingNumberReadOnly = (delivery, user) => {
  return !userHasDeliverySchedulingFeature(user)
      || isDeliveryComplete(delivery)
}

export const isActualTimesReadOnly = (user) => {
  return !userHasDeliverySchedulingFeature(user)
}

export const isTitleReadOnly = (user) => {
  return !userHasDeliverySchedulingFeature(user)
}

export const isNotesReadOnly = (user) => {
  return !userHasDeliverySchedulingFeature(user)
}

export const isDeliveryTypeNoteDialogReadOnly = (user) => {
  return !userHasDeliverySchedulingFeature(user)
}

export const isDeliveryOperationComplete = (deliveryOperation) => {
  return deliveryOperation.map(op => get(op, 'deliveryOperationStatus')).includes('COMPLETE')
}

export const DeliveryOperationAction = {
  START: 'START',
  STOP: 'STOP',
  ON_HOLD: 'ON_HOLD',
  COMPLETE: 'COMPLETE',
  ADD: 'ADD',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
}

export const getFilteredWorkCentres = (workCentres, deliveryFilters) => {
  const workCentreIds = get(deliveryFilters, 'workCentres', [])
  if (isEmpty(workCentreIds)) return workCentres
  return workCentres.filter(centre => workCentreIds.includes(centre.id))
}

export const isTotalQuantityZero = (items) => {
  const totalQuantity = items.reduce((acc, item) => acc + item.quantity, 0)
  return totalQuantity === 0
}
